/**
 * PostHog Event Tracking Utilities
 *
 * Provides typed methods for tracking business events
 * with graceful error handling.
 */

/**
 * Safely capture an event in PostHog
 * @param {string} eventName - The event name
 * @param {object} properties - Event properties
 * @param {Window} window - The window object (for testing)
 */
function safeCapture(eventName, properties = {}, windowObj = window) {
  try {
    if (windowObj.$posthog && !windowObj.$posthog.has_opted_out_capturing()) {
      windowObj.$posthog.capture(eventName, properties);
    }
  } catch (error) {
    console.error(`[PostHog] Failed to capture event "${eventName}":`, error);
  }
}

/**
 * Monitor Events
 */
export const monitorEvents = {
  created: (monitor) => {
    safeCapture("monitor_created", {
      monitor_type: monitor.type,
      monitor_interval: monitor.interval,
      monitor_name: monitor.name,
    });
  },

  updated: (monitor, changes) => {
    safeCapture("monitor_updated", {
      monitor_id: monitor._id || monitor.id,
      monitor_type: monitor.type,
      changed_fields: Object.keys(changes || {}),
    });
  },

  deleted: (monitorId) => {
    safeCapture("monitor_deleted", {
      monitor_id: monitorId,
    });
  },

  paused: (monitorId) => {
    safeCapture("monitor_paused", {
      monitor_id: monitorId,
    });
  },

  resumed: (monitorId) => {
    safeCapture("monitor_resumed", {
      monitor_id: monitorId,
    });
  },
};

/**
 * User Authentication Events
 */
export const userEvents = {
  signedUp: (method, user) => {
    safeCapture("user_signed_up", {
      signup_method: method,
      email: user.email,
      plan: user.plan,
    });
  },

  loggedIn: (method, user) => {
    safeCapture("user_logged_in", {
      login_method: method,
      email: user.email,
    });
  },

  loggedOut: () => {
    safeCapture("user_logged_out");
  },
};

/**
 * Revenue Events
 */
export const revenueEvents = {
  subscriptionStarted: (plan, amount, interval) => {
    safeCapture("subscription_started", {
      plan: plan,
      amount: amount,
      billing_interval: interval,
      currency: "USD",
    });
  },

  subscriptionCancelled: (plan, reason) => {
    safeCapture("subscription_cancelled", {
      plan: plan,
      reason: reason || "not_specified",
    });
  },

  planUpgraded: (fromPlan, toPlan, amount) => {
    safeCapture("plan_upgraded", {
      from_plan: fromPlan,
      to_plan: toPlan,
      amount: amount,
      currency: "USD",
    });
  },
};

/**
 * Alert Events
 */
export const alertEvents = {
  channelAdded: (channelType) => {
    safeCapture("alert_channel_added", {
      channel_type: channelType,
    });
  },

  channelRemoved: (channelType) => {
    safeCapture("alert_channel_removed", {
      channel_type: channelType,
    });
  },

  channelTested: (channelType, success) => {
    safeCapture("alert_channel_tested", {
      channel_type: channelType,
      success: success,
    });
  },

  ruleUpdated: (ruleType) => {
    safeCapture("alert_rule_updated", {
      rule_type: ruleType,
    });
  },
};

/**
 * Status Page Events
 */
export const statusPageEvents = {
  created: (pageName) => {
    safeCapture("status_page_created", {
      page_name: pageName,
    });
  },

  published: (pageId) => {
    safeCapture("status_page_published", {
      page_id: pageId,
    });
  },

  customized: (pageId, customizationType) => {
    safeCapture("status_page_customized", {
      page_id: pageId,
      customization_type: customizationType,
    });
  },
};

/**
 * Identify user in PostHog
 * @param {string} userId - User ID
 * @param {object} user - User object with properties
 */
export function identifyUser(userId, user = {}) {
  try {
    if (window.$posthog && !window.$posthog.has_opted_out_capturing()) {
      window.$posthog.identify(userId, {
        email: user.email,
        name: user.username || user.name,
        plan: user.plan || "free",
        role: user.role,
        monitor_count: user.monitorCount || 0,
        created_at: user.createdAt || new Date().toISOString(),
        company: {
          id: user.companyId || user.userid || userId,
          name: user.companyName || "Individual",
        },
      });
      // Ensure session recording continues after identify
      window.$posthog.startSessionRecording();
    }
  } catch (error) {
    console.error("[PostHog] Failed to identify user:", error);
  }
}

/**
 * Reset PostHog (on logout)
 */
export function resetPostHog() {
  try {
    if (window.$posthog) {
      window.$posthog.reset();
    }
  } catch (error) {
    console.error("[PostHog] Failed to reset:", error);
  }
}

/**
 * Update user properties
 * @param {object} properties - Properties to update
 */
export function updateUserProperties(properties) {
  try {
    if (window.$posthog && !window.$posthog.has_opted_out_capturing()) {
      window.$posthog.people.set(properties);
    }
  } catch (error) {
    console.error("[PostHog] Failed to update user properties:", error);
  }
}

/**
 * Track page view manually
 * @param {string} path - Current path
 * @param {string} name - Route name
 * @param {string} from - Previous path
 */
export function trackPageView(path, name, from) {
  safeCapture("$pageview", {
    $current_url: window.location.href,
    path: path,
    name: name,
    from: from,
  });
}

/**
 * Check if PostHog is available and user has not opted out
 */
export function isTrackingEnabled() {
  try {
    return window.$posthog && !window.$posthog.has_opted_out_capturing();
  } catch (error) {
    return false;
  }
}

export default {
  monitorEvents,
  userEvents,
  revenueEvents,
  alertEvents,
  statusPageEvents,
  identifyUser,
  resetPostHog,
  updateUserProperties,
  trackPageView,
  isTrackingEnabled,
};
